import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import axios, {AxiosError} from "axios";
import i18next from "i18next";
import {CornyBakersEnv} from "../../../../../shared/environment/features/CornyBakersEnvironment";
import {
    WebshopNotificationDataModel,
    WebshopNotificationSliceModel
} from "./webshopNotificationSliceModel";

const API_BASE_URL = CornyBakersEnv.API_BASE_URL;

const getApiUrl = (path: string) => {
    const lang = i18next.language;
    const formatLang = lang === 'nl' ? '' : `/${lang}`;
    return `${API_BASE_URL}${formatLang}${path}`;
};

export const fetchUnreadWebshopNotifications = createAsyncThunk(
    "webshopNotification/fetchUnread",
    async (_, thunkAPI) => {
        try {
            const response = await axios.get(getApiUrl('/api/webshop-notifications/unread'), {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            return response.data as WebshopNotificationDataModel[];
        } catch (error: unknown) {
            if (error instanceof AxiosError) {
                return thunkAPI.rejectWithValue(error.message);
            }
            return thunkAPI.rejectWithValue('Something went wrong, please try again');
        }
    }
);

export const markWebshopNotificationSeen = createAsyncThunk(
    "webshopNotification/markSeen",
    async (notificationId: number, thunkAPI) => {
        try {
            await axios.post(getApiUrl(`/api/webshop-notifications/${notificationId}/seen`), {}, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            return notificationId;
        } catch (error: unknown) {
            if (error instanceof AxiosError) {
                return thunkAPI.rejectWithValue(error.message);
            }
            return thunkAPI.rejectWithValue('Something went wrong, please try again');
        }
    }
);

const initialState = {
    notifications: [],
    loading: false,
    loaded: false,
    seenLoading: false,
    error: null,
} as WebshopNotificationSliceModel;

const webshopNotificationSlice = createSlice({
    name: "webshopNotification",
    initialState,
    reducers: {
        resetWebshopNotifications: (state) => {
            state.notifications = [];
            state.loading = false;
            state.loaded = false;
            state.seenLoading = false;
            state.error = null;
        }
    },
    extraReducers(builder) {
        builder.addCase(fetchUnreadWebshopNotifications.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchUnreadWebshopNotifications.fulfilled, (state, action) => {
            state.loading = false;
            state.loaded = true;
            state.notifications = action.payload ?? [];
        });
        builder.addCase(fetchUnreadWebshopNotifications.rejected, (state, action) => {
            state.loading = false;
            state.loaded = true;
            state.error = (action.payload as string) ?? 'Something went wrong, please try again';
        });
        builder.addCase(markWebshopNotificationSeen.pending, (state) => {
            state.seenLoading = true;
            state.error = null;
        });
        builder.addCase(markWebshopNotificationSeen.fulfilled, (state, action) => {
            state.seenLoading = false;
            state.notifications = state.notifications.filter(notification => notification.id !== action.payload);
        });
        builder.addCase(markWebshopNotificationSeen.rejected, (state, action) => {
            state.seenLoading = false;
            state.error = (action.payload as string) ?? 'Something went wrong, please try again';
        });
    }
});

export const {
    resetWebshopNotifications,
} = webshopNotificationSlice.actions;

export default webshopNotificationSlice.reducer;
